import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import "./Navbar.css";

export default function Navbar() {
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState(
    localStorage.getItem("selectedLanguage") || "en"
  );
  const [langOpen, setLangOpen] = useState(false);

  // Separate dropdown states ✅
  const [mobileToursOpen, setMobileToursOpen] = useState(false);
  const [mobilePackagesOpen, setMobilePackagesOpen] = useState(false);

  const langRef = useRef(null);

  const languages = [
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "fr", label: "French", flag: "🇫🇷" },
    { code: "de", label: "German", flag: "🇩🇪" },
    { code: "ar", label: "Arabic", flag: "🇸🇦" },
    { code: "zh-CN", label: "Chinese", flag: "🇨🇳" },
  ];

  // Close all menus
  const closeMenu = () => {
    setOpen(false);
    setMobileToursOpen(false);
    setMobilePackagesOpen(false);
    setLangOpen(false);
  };

  // Google Translate trigger (safe)
  const applyLanguage = (code) => {
    const select = document.querySelector(".goog-te-combo");
    if (!select) {
      console.warn("Google Translate not ready");
      return;
    }
    select.value = code;
    select.dispatchEvent(new Event("change"));
  };

  const handleLanguageChange = (code) => {
    setLanguage(code);
    localStorage.setItem("selectedLanguage", code);
    applyLanguage(code);
    setLangOpen(false);
  };

  // Close language dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Re-apply translation on route change + close menu
  useEffect(() => {
    const savedLang = localStorage.getItem("selectedLanguage") || "en";
    setLanguage(savedLang);

    setTimeout(() => applyLanguage(savedLang), 300);

    // Auto close menus on navigation ✅
    closeMenu();
  }, [location]);

  // Toggle body class for mobile menu
  useEffect(() => {
    if (open) document.body.classList.add("nav-open");
    else document.body.classList.remove("nav-open");
  }, [open]);

  return (
    <>
      <header className="topnav-professional">
        {/* Logo */}
        <NavLink to="/" className="brand" onClick={closeMenu}>
          <img src="/images/savilogo.png" alt="Savi Guide Tours Logo" />
          <span>Savi Guide Tours</span>
        </NavLink>

        {/* Desktop Navigation */}
        <nav className="nav-links-desktop">
          <NavLink to="/" end className={({ isActive }) => isActive ? "active" : ""}>
            Home
          </NavLink>

          {/* Tours Dropdown */}
          <div className="dropdown">
            <span className="dropdown-trigger">Tours ▾</span>
            <div className="dropdown-menu">
              <NavLink to="/day-tours" onClick={closeMenu}>Day Tours</NavLink>
              <NavLink to="/water-activities" onClick={closeMenu}>Water Activities</NavLink>
            </div>
          </div>

          {/* ✅ Packages Dropdown */}
          <div className="dropdown">
            <span className="dropdown-trigger">Packages ▾</span>
            <div className="dropdown-menu">
              <NavLink to="/packages" onClick={closeMenu}>All Packages</NavLink>
              <NavLink to="/packages/safari" onClick={closeMenu}>Safari Packages</NavLink>
            </div>
          </div>

          <NavLink to="/hotels" onClick={closeMenu}>Hotels</NavLink>
          <NavLink to="/transfers" onClick={closeMenu}>Transfers</NavLink>
          <NavLink to="/car-rentals" onClick={closeMenu}>Car Rentals</NavLink>
          <NavLink to="/about" onClick={closeMenu}>About</NavLink>

          <NavLink to="/contact" className="cta" onClick={closeMenu}>
            Contact
          </NavLink>
        </nav>

        {/* Language Selector */}
        <div className="topnav-lang" ref={langRef}>
          <button
            className="lang-btn"
            onClick={() => setLangOpen(!langOpen)}
          >
            {language.toUpperCase()} ▾
          </button>

          {langOpen && (
            <div className="lang-menu">
              {languages.map((l) => (
                <button
                  key={l.code}
                  className={l.code === language ? "active" : ""}
                  onClick={() => handleLanguageChange(l.code)}
                >
                  {l.flag} {l.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Hamburger */}
        <button
          className={`hamburger ${open ? "open" : ""}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle navigation"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </header>

      {/* Mobile Sidebar */}
      <aside className={`sidebar-professional ${open ? "active" : ""}`}>
        <div className="sheet-handle"></div>

        <nav className="nav-links-mobile">
          <NavLink to="/" end onClick={closeMenu}>Home</NavLink>

          {/* Tours Mobile */}
          <div className="mobile-dropdown">
            <span onClick={() => setMobileToursOpen(!mobileToursOpen)}>
              Tours ▾
            </span>

            {mobileToursOpen && (
              <div className="mobile-submenu">
                <NavLink to="/day-tours" onClick={closeMenu}>Day Tours</NavLink>
                <NavLink to="/water-activities" onClick={closeMenu}>Water Activities</NavLink>
              </div>
            )}
          </div>

          {/* ✅ Packages Mobile */}
          <div className="mobile-dropdown">
            <span onClick={() => setMobilePackagesOpen(!mobilePackagesOpen)}>
              📦 Packages ▾
            </span>

            {mobilePackagesOpen && (
              <div className="mobile-submenu">
                <NavLink to="/packages" onClick={closeMenu}>All Packages</NavLink>
                <NavLink to="/packages/safari" onClick={closeMenu}>Safari Packages</NavLink>
              </div>
            )}
          </div>

          <NavLink to="/hotels" onClick={closeMenu}>Hotels</NavLink>
          <NavLink to="/transfers" onClick={closeMenu}>Transfers</NavLink>
          <NavLink to="/car-rentals" onClick={closeMenu}>Car Rentals</NavLink>
          <NavLink to="/about" onClick={closeMenu}>About</NavLink>
          <NavLink to="/contact" onClick={closeMenu}>Contact</NavLink>
        </nav>
      </aside>

      {/* Overlay */}
      {open && <div className="overlay" onClick={closeMenu}></div>}
    </>
  );
}